import { replace } from 'react-router-dom';
import { match, P } from 'ts-pattern';
import { z } from 'zod';
import { zfd } from 'zod-form-data';
import { AppState, AppStore } from '~/stores/app';
import { ClaimType } from '~/utils/constants';
import {
  makeAction,
  makeLoader,
  matchShippingProtectionPath,
  redirect,
} from '~/utils/routing';
import { addressFormSchema, claimAddressSchema } from '~/utils/schemas';
import { isAddressPhoneRequired } from '~/utils/theme';
import type { SetNonNullable } from '~/utils/types';

const handleError = (request: Request) => (data: unknown) =>
  new Error('Invalid state for edit address', {
    cause: { data, request },
  });

export const { loader, useLoaderData } = makeLoader(
  ({ request, params }, appStore) =>
    match({
      isShippingProtectionClaim: matchShippingProtectionPath(request),
      state: appStore.getState(),
    })
      .with(
        {
          isShippingProtectionClaim: P.nonNullable,
          state: { shippingProtectionClaim: { status: 'new' } },
        },
        ({ state: { shippingProtectionClaim, order } }) => ({
          address: shippingProtectionClaim.address ?? order?.shippingAddress,
          requirePhone: false,
        }),
      )
      .with(
        {
          isShippingProtectionClaim: P.nonNullable,
          state: { shippingProtectionClaim: { status: 'completed' } },
        },
        () => replace(`/${params.store ?? ''}`),
      )

      .with(
        {
          isShippingProtectionClaim: P.nullish,
          state: {
            settings: P.nonNullable,
            crewClaim: { status: 'new' },
          },
        },
        ({ state: { settings, crewClaim, order } }) => ({
          address: crewClaim.address ?? order?.shippingAddress,
          requirePhone: isAddressPhoneRequired(
            settings.theme,
            crewClaim.claimType,
          ),
        }),
      )
      .with(
        {
          isShippingProtectionClaim: P.nullish,
          state: {
            crewClaim: { status: 'completed' },
          },
        },
        () => replace(`/${params.store}/order/${params.idFromPlatform}`),
      )
      .otherwise(handleError(request)),
);

type ActionReturnType = ReturnType<Parameters<typeof makeAction>[0]>;
const parseAddressForm = ({
  formData,
  requirePhone,
  onSuccess,
}: {
  formData: FormData;
  requirePhone: boolean;
  onSuccess: (address: z.infer<typeof addressFormSchema>) => ActionReturnType;
}) => {
  // zfd.text doesn't play nice with addressFormSchema.required({ phone: true })
  // so we unwrap the phone field from the claimAddressSchema and use it directly
  const schema =
    requirePhone ?
      addressFormSchema.extend({
        phone: zfd.text(claimAddressSchema.shape.phone.unwrap()),
      })
    : addressFormSchema;

  return match(zfd.formData({ address: schema }).safeParse(formData))
    .returnType<ActionReturnType>() // help assert the false case is inferred as ActionResult
    .with({ success: true }, ({ data }) => onSuccess(data.address))
    .with({ success: false }, ({ error }) => ({
      ok: false,
      message: error.message,
    }))
    .exhaustive();
};

const handleShippingProtectionRoute =
  (appStore: AppStore) => (formData: FormData) =>
    parseAddressForm({
      formData,
      requirePhone: false,
      onSuccess: (address) => {
        appStore.dispatch(
          appStore.actions.shippingProtectionClaim.setAddress(address),
        );

        return redirect('../review');
      },
    });

const handleCrewClaimRoute =
  (appStore: AppStore) =>
  ({
    state: { crewClaim, settings },
    formData,
  }: {
    state: SetNonNullable<AppState, 'settings' | 'crewClaim'>;
    formData: FormData;
  }) =>
    parseAddressForm({
      formData,
      requirePhone: isAddressPhoneRequired(settings.theme, crewClaim.claimType),
      onSuccess: (address) => {
        appStore.dispatch(appStore.actions.crewClaim.setAddress(address));
        const nextStep =
          crewClaim.claimType === ClaimType.warranty ?
            `../../warranty-review`
          : `../../return-methods`;
        return redirect(nextStep);
      },
    });

export const { action, useActionResult } = makeAction(
  async ({ request, formData }, appStore) =>
    match({
      isShippingProtectionClaim: matchShippingProtectionPath(request),
      state: appStore.getState(),
      formData,
    })
      .with(
        { isShippingProtectionClaim: P.nonNullable, formData: P.select() },
        handleShippingProtectionRoute(appStore),
      )
      .with(
        {
          isShippingProtectionClaim: P.nullish,
          state: { settings: P.nonNullable, crewClaim: P.nonNullable },
        },
        handleCrewClaimRoute(appStore),
      )
      .otherwise(handleError(request)),
);
